import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CalendarDays, Plus, Loader2, Archive, Copy, Pencil, Share2, WifiOff, RefreshCw, GraduationCap, RotateCcw, Map, ClipboardPaste, SlidersHorizontal, AlertTriangle, Target, PartyPopper, ScanLine, Undo2, Award,
} from 'lucide-react';
import ExamFormDialog from '@/components/premium/ExamFormDialog';
import StudyPlanner from '@/components/premium/StudyPlanner';
import HolidayCalendarDialog from '@/components/premium/HolidayCalendarDialog';
import ExamResultDialog from '@/components/premium/ExamResultDialog';
import PortionMapperDialog from '@/components/premium/PortionMapperDialog';
import ExamOverridesDialog from '@/components/premium/ExamOverridesDialog';
import { Hint, InfoDot } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';
import { API_BASE, apiPost, authedFetch } from '@/lib/api';
import {
  adoptExam, archiveExam, duplicateExam, examModuleStatus, fetchExamStudents, fetchExams, previewSharedExam, restoreExam,
} from '@/lib/premiumExams';

// Each state owns a colour family so a parent can read the list at a glance
// instead of scanning identical white cards.
const stateStyles = {
  upcoming: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  in_progress: 'bg-amber-100 text-amber-800 ring-amber-200',
  completed: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  archived: 'bg-slate-200 text-slate-500 ring-slate-300',
};

const stateAccent = {
  upcoming: { bar: 'from-indigo-500 to-violet-500', tint: 'from-indigo-50/80', ring: 'ring-indigo-100' },
  in_progress: { bar: 'from-amber-500 to-orange-500', tint: 'from-amber-50/80', ring: 'ring-amber-100' },
  completed: { bar: 'from-emerald-500 to-teal-500', tint: 'from-emerald-50/80', ring: 'ring-emerald-100' },
  archived: { bar: 'from-slate-400 to-slate-500', tint: 'from-slate-100/80', ring: 'ring-slate-200' },
};

// Subject chips cycle through a fixed palette so the same subject keeps the
// same colour on every card.
const SUBJECT_TONES = [
  'bg-sky-50 text-sky-700 border-sky-200',
  'bg-rose-50 text-rose-700 border-rose-200',
  'bg-violet-50 text-violet-700 border-violet-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-amber-50 text-amber-800 border-amber-200',
  'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
];
const subjectTone = (name) => SUBJECT_TONES[
  [...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0) % SUBJECT_TONES.length
];

// Premium renders every date in Asia/Kolkata, notices included.
const istDate = (iso) => (iso
  ? new Date(`${iso}T00:00:00+05:30`).toLocaleDateString('en-IN',
    { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
  : '');


function Countdown({ exam }) {
  if (exam.state === 'archived') return <span className="text-[11px] text-slate-400 font-body">Archived</span>;
  if (exam.countdown_days == null) return null;
  if (exam.countdown_days > 0) {
    return <span className="text-[11px] font-heading text-slate-700" data-testid={`exam-countdown-${exam.exam_id}`}>{exam.countdown_days} day{exam.countdown_days === 1 ? '' : 's'} to go</span>;
  }
  if (exam.state === 'in_progress') return <span className="text-[11px] font-heading text-amber-600">Happening now</span>;
  return <span className="text-[11px] font-body text-slate-400">Done</span>;
}

function ExamCard({ exam, mapperLive, importsLive, onEdit, onMap, onPlan, onResults, onOverrides, onDuplicate, onArchive, onRestore }) {
  const accent = stateAccent[exam.state] || stateAccent.upcoming;
  return (
    <div className={`group relative overflow-hidden rounded-2xl bg-white ring-1 ${accent.ring} shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_12px_28px_-12px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-all duration-300`} data-testid={`exam-card-${exam.exam_id}`}>
      {/* colour spine — tells the state apart before you read a word */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${accent.bar}`} />
      <div className={`bg-gradient-to-r ${accent.tint} to-transparent pl-5 pr-4 pt-4 pb-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading text-[15px] font-semibold text-slate-900 truncate">{exam.name}</p>
          <p className="text-[11px] text-slate-500 font-body mt-0.5 flex items-center gap-1">
            <CalendarDays className="w-3 h-3 text-slate-400" />
            {exam.assessment_label} · {exam.start_date_ist} – {exam.end_date_ist}
            {exam.student_name ? ` · ${exam.student_name}` : ''}
          </p>
        </div>
        <span className={`text-[10px] font-heading uppercase tracking-wider px-2 py-1 rounded-full ring-1 flex-shrink-0 ${stateStyles[exam.state] || stateStyles.upcoming}`}>
          {exam.state.replace('_', ' ')}
        </span>
      </div>
      </div>

      <div className="pl-5 pr-4 pb-4">
      <div className="flex flex-wrap gap-1.5">
        {(exam.subjects || []).length === 0 && (
          <span className="text-[11px] text-slate-400 font-body">No portion yet — open "Map portion" to pick the subjects and tick their chapters.</span>
        )}
        {(exam.subjects || []).map(s => (
          <span key={s.subject} className={`text-[11px] font-body px-2 py-0.5 rounded-full border ${subjectTone(s.subject)}`}>
            {s.subject} · <span className="font-heading">{s.mapping_completeness}%</span>
          </span>
        ))}
      </div>

      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden ring-1 ring-slate-100">
        <div className={`h-full rounded-full bg-gradient-to-r ${exam.mapping_completeness >= 100 ? 'from-emerald-400 to-teal-500' : exam.mapping_completeness > 0 ? 'from-indigo-500 to-violet-500' : 'from-slate-200 to-slate-200'} transition-all duration-700`}
          style={{ width: `${Math.max(exam.mapping_completeness, 2)}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-slate-500 font-body inline-flex items-center gap-1" data-testid={`exam-mapping-${exam.exam_id}`}>
          Portion mapped {exam.mapping_completeness}%
          <InfoDot text={HELP.exams.mappedPct} testId={`hint-mapped-${exam.exam_id}`} />
        </span>
        <Hint text={HELP.exams.countdown}><Countdown exam={exam} /></Hint>
      </div>

      <div className="flex items-center gap-1.5 mt-3 flex-wrap pt-3 border-t border-dashed border-slate-200">
        {exam.state !== 'archived' ? (
          <>
            <Hint text={HELP.exams.editMap}>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-edit-${exam.exam_id}`} onClick={() => onEdit(exam)}>
                <Pencil className="w-3 h-3 mr-1" /> Edit dates
              </Button>
            </Hint>
            {mapperLive && (
              <Hint text={HELP.exams.mapPortion}>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-map-${exam.exam_id}`} onClick={() => onMap(exam)}>
                  <Map className="w-3 h-3 mr-1" /> Map portion
                </Button>
              </Hint>
            )}
            {mapperLive && (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-plan-${exam.exam_id}`} onClick={() => onPlan(exam)}>
                <Target className="w-3 h-3 mr-1" /> Study planner
              </Button>
            )}
            {mapperLive && (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-results-${exam.exam_id}`} onClick={() => onResults(exam)}>
                <Award className="w-3 h-3 mr-1" /> Marks
              </Button>
            )}
            {importsLive && (
              <Hint text={HELP.exams.differences}>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-overrides-${exam.exam_id}`} onClick={() => onOverrides(exam)}>
                  <SlidersHorizontal className="w-3 h-3 mr-1" /> Differences
                </Button>
              </Hint>
            )}
            <Hint text={HELP.exams.duplicate}>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-duplicate-${exam.exam_id}`} onClick={() => onDuplicate(exam)}>
                <Copy className="w-3 h-3 mr-1" /> Duplicate
              </Button>
            </Hint>
            <Hint text={HELP.exams.archive}>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-archive-${exam.exam_id}`} onClick={() => onArchive(exam)}>
                <Archive className="w-3 h-3 mr-1" /> Archive
              </Button>
            </Hint>
          </>
        ) : (
          <Hint text={HELP.exams.restore}>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exam-restore-${exam.exam_id}`} onClick={() => onRestore(exam)}>
              <RotateCcw className="w-3 h-3 mr-1" /> Restore
            </Button>
          </Hint>
        )}
        {exam.share_code && (
          <Hint text={HELP.exams.myShareCode}>
            <button data-testid={`exam-share-${exam.exam_id}`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(exam.share_code);
                  toast.success(`Share code ${exam.share_code} copied`);
                } catch {
                  toast.message(`Copy this code manually: ${exam.share_code}`);
                }
              }}
              className="h-7 inline-flex items-center gap-1 text-[11px] font-heading text-slate-500 hover:text-slate-800 px-2 rounded-lg hover:bg-slate-100 transition-colors">
              <Share2 className="w-3 h-3" /> {exam.share_code}
            </button>
          </Hint>
        )}
      </div>
      </div>
    </div>
  );
}

export default function ExamsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('active');
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offline, setOffline] = useState(!navigator.onLine);
  const [moduleLive, setModuleLive] = useState(null);
  const [mapperLive, setMapperLive] = useState(false);
  const [importsLive, setImportsLive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareCode, setShareCode] = useState('');
  const [mapping, setMapping] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const noticeFileRef = useRef(null);
  const [overriding, setOverriding] = useState(null);
  const [notice, setNotice] = useState('');
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [adopting, setAdopting] = useState(false);
  useScreenTip('exams', HELP.tips.exams, moduleLive === true);

  useEffect(() => {
    const on = () => setOffline(false); const off = () => setOffline(true);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    examModuleStatus().then(s => {
      setModuleLive(s.module_enabled && s.entitled);
      // Phase 03/04 switches are independent — hide their controls entirely
      // while they are off rather than letting them 404 on click.
      setMapperLive(!!s.portion_mapper);
      setImportsLive(!!s.exam_imports);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [list, roster] = await Promise.all([fetchExams(status, studentId), fetchExamStudents()]);
      setExams(list.exams || []);
      setStudents(roster.students || []);
      if (!studentId && (roster.students || []).length) setStudentId(roster.students[0].student_id);
    } catch (e) {
      setError(e?.message === 'NETWORK_ERROR' ? 'You appear to be offline — we will retry when the connection is back.' : 'We could not load your exams. Please try again.');
    }
    setLoading(false);
  }, [status, studentId]);

  useEffect(() => { if (moduleLive) load(); }, [moduleLive, load]);

  const student = students.find(s => s.student_id === studentId) || students[0];

  const onDuplicate = async (exam) => {
    try {
      const copy = await duplicateExam(exam.exam_id, {
        name: `${exam.name} (copy)`, start_date: exam.start_date, end_date: exam.end_date,
      });
      toast.success('Duplicated — set the new dates');
      setEditing(copy); setFormOpen(true); load();
    } catch (e) { toast.error(e?.message || 'Could not duplicate'); }
  };

  // Scroll to the planner only when it is opened for a different exam — a ref
  // callback did this on every render, so Archive/Restore/anything yanked the
  // page down to the planner.
  const plannerRef = useRef(null);
  const scrolledFor = useRef('');
  useEffect(() => {
    const id = planning?.exam_id || '';
    if (!id) { scrolledFor.current = ''; return; }
    if (scrolledFor.current === id) return;
    scrolledFor.current = id;
    plannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [planning]);

  const onArchive = async (exam) => {
    try { await archiveExam(exam.exam_id); toast.success('Exam archived'); load(); }
    catch (e) { toast.error(e?.message || 'Could not archive'); }
  };

  const onRestore = async (exam) => {
    try { await restoreExam(exam.exam_id); toast.success('Exam restored'); load(); }
    catch (e) { toast.error(e?.message || 'Could not restore'); }
  };

  // Reviewable import: parse a pasted notice into PROPOSALS first — nothing is
  // created until the parent confirms.
  const parseNotice = async () => {
    if (!notice.trim()) { toast.message('Paste the exam notice first'); return; }
    try {
      setDraft(await apiPost('/premium/v1/exams/imports', { raw_text: notice, child_id: studentId }));
    } catch (e) { toast.error(e?.message || 'Could not read that notice'); }
  };

  // Phase 04 — the school notice usually arrives as a PDF or a WhatsApp photo.
  // The AI only PROPOSES; every proposal is matched to approved nodes and
  // anything uncertain becomes an exception the parent resolves.
  const scanNotice = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (studentId) fd.append('child_id', studentId);
      const res = await authedFetch(`${API_BASE}/premium/v1/exams/imports/scan`, { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'We could not read that notice.');
      setDraft(body);
      toast.success('Notice read — review the proposal before anything is created');
    } catch (e) { toast.error(e?.message || 'Could not read that notice'); }
    setScanning(false);
  };

  const rollbackImport = async () => {
    try {
      const res = await apiPost(`/premium/v1/exams/imports/${draft.import_id}/rollback`, {});
      toast.success(res.message || 'Rolled back');
      setDraft(null); load();
    } catch (e) { toast.error(e?.message || 'Could not roll that back'); }
  };

  const confirmDraft = async () => {
    try {
      // A scanned notice usually carries the name and dates — send blanks and
      // let the server fall back to what it read off the page.
      const res = await apiPost(`/premium/v1/exams/imports/${draft.import_id}/confirm`, {
        name: draft.proposed_name || '', assessment_type: 'term_exam',
        start_date: draft.proposed_start_date || '', end_date: draft.proposed_end_date || '',
      });
      const dated = !!(draft.proposed_start_date && draft.proposed_end_date);
      toast.success(dated ? 'Exam created from the notice' : 'Exam created — set the real dates now');
      setDraft(null); setNotice(''); load();
      if (!dated) { setEditing(res.exam); setFormOpen(true); }
    } catch (e) { toast.error(e?.message || 'Could not confirm the import'); }
  };

  // Adoption is a two-step review: fetch the blueprint first so the parent can
  // see exactly what would be copied, then copy only on explicit confirmation.
  const onPreview = async () => {
    if (!shareCode.trim()) { toast.message('Enter the share code you were given'); return; }
    try {
      setPreview(await previewSharedExam(shareCode.trim(), studentId));
    } catch (e) { toast.error(e?.message || 'That code did not match any exam'); }
  };

  const onAdopt = async () => {
    setAdopting(true);
    try {
      await adoptExam({ share_code: preview.share_code, child_id: studentId });
      toast.success(`"${preview.name}" adopted — the portion is now yours to adjust`);
      setPreview(null); setShareCode(''); load();
    } catch (e) { toast.error(e?.message || 'That code did not work'); }
    setAdopting(false);
  };

  if (moduleLive === null) {
    return <div className="max-w-3xl mx-auto px-4 pt-10" data-testid="exams-loading"><p className="flex items-center gap-2 text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin" /> Loading exams…</p></div>;
  }
  if (!moduleLive) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-16 text-center" data-testid="exams-unavailable">
        <GraduationCap className="w-10 h-10 text-slate-300 mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-3">Exam planning is a Premium feature</h1>
        <p className="text-sm text-slate-500 font-body mt-1.5">Plan every unit test, term exam and annual with the portion mapped chapter by chapter.</p>
        <Button className="mt-4 bg-gradient-to-r from-indigo-500 to-violet-500 text-white" data-testid="exams-upgrade-btn"
          onClick={() => navigate('/subscription')}>See Premium</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-28 sm:pb-12" data-testid="exams-page">
      {/* Hero band — gives the page an identity instead of opening on white */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 px-5 py-5 sm:px-6 sm:py-6 shadow-lg shadow-indigo-500/20">
        <div className="absolute -right-8 -top-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -left-6 -bottom-12 w-40 h-40 rounded-full bg-fuchsia-300/20 blur-2xl" />
        <div className="relative flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-white/70">Academic planner</p>
          <h1 className="font-heading text-2xl font-bold text-white mt-0.5">Exams</h1>
          <p className="text-xs text-indigo-100 font-body mt-1 max-w-md">Every mission, revision block and report traces back to an exam and its portion.</p>
          <button data-testid="exams-open-cockpit" onClick={() => navigate('/cockpit')}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-heading bg-white/15 hover:bg-white/25 transition-colors text-white px-2.5 py-1 rounded-full backdrop-blur-sm">
            Open today's cockpit →
          </button>
        </div>
        <Hint text={HELP.exams.addExam} side="left">
          <Button size="sm" data-testid="exam-create-btn"
            className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm font-heading"
            onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add exam
          </Button>
        </Hint>
        </div>
      </div>

      {offline && (
        <p className="mt-3 flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 font-body" data-testid="exams-offline">
          <WifiOff className="w-3.5 h-3.5" /> You are offline. Exams shown may be out of date.
        </p>
      )}

      <div className="flex items-center gap-2 mt-4 flex-wrap rounded-2xl bg-white ring-1 ring-slate-100 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-3 py-2.5">
        {students.length > 1 && (
          <Hint text={HELP.exams.studentSelect}>
            <select data-testid="exam-student-select" value={studentId} onChange={e => setStudentId(e.target.value)}
              className="p-2 rounded-lg border border-slate-200 text-xs font-body">
              {students.map(s => <option key={s.student_id} value={s.student_id}>{s.name} · Class {s.class_level}</option>)}
            </select>
          </Hint>
        )}
        <Hint text={HELP.exams.tabs}>
          <div className="flex rounded-full bg-slate-100 p-0.5">
            {['active', 'archived'].map(tab => (
              <button key={tab} data-testid={`exam-tab-${tab}`} onClick={() => setStatus(tab)}
                className={`px-3.5 py-1.5 rounded-full text-[11px] font-heading capitalize transition-all ${status === tab
                  ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'}`}>
                {tab}
              </button>
            ))}
          </div>
        </Hint>
        <div className="flex items-center gap-1.5 ml-auto">
          <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="exams-holidays-btn"
            onClick={() => setHolidaysOpen(true)}>
            <PartyPopper className="w-3 h-3 mr-1" /> Holidays
          </Button>
          <span className="hidden lg:inline-flex items-center gap-1 text-[11px] font-body text-slate-400 mr-1">
            Got a code from another parent?
            <InfoDot text={HELP.exams.shareCodeInput} title="Adopt a shared exam" side="bottom" align="end" testId="hint-share-code" />
          </span>
          <Hint text={HELP.exams.shareCodeInput} side="bottom" align="end">
            <Input data-testid="exam-adopt-input" value={shareCode} onChange={e => setShareCode(e.target.value.toUpperCase())}
              placeholder="Share code" className="h-8 w-28 rounded-lg text-xs" />
          </Hint>
          <Hint text={HELP.exams.adoptBtn} side="bottom" align="end">
            <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="exam-adopt-btn" onClick={onPreview}>Adopt</Button>
          </Hint>
        </div>
      </div>

      {/* Phase 04 — review a shared blueprint BEFORE anything is copied */}
      {preview && (
        <div className="mt-3 rounded-2xl bg-white ring-1 ring-indigo-200 shadow-[0_8px_24px_-16px_rgba(79,70,229,0.5)] overflow-hidden" data-testid="exam-adopt-preview">
          <div className="h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
          <div className="p-3 bg-gradient-to-br from-indigo-50/70 to-transparent">
          <p className="text-[10px] font-heading uppercase tracking-widest text-indigo-500 inline-flex items-center gap-1">
            Review before you adopt
            <InfoDot text={HELP.exams.adoptPreview} side="right" testId="hint-adopt-preview" />
          </p>
          <p className="font-heading text-[14px] font-semibold text-slate-900 mt-1" data-testid="adopt-preview-name">{preview.name}</p>
          <p className="text-[11px] text-slate-500 font-body">
            {preview.assessment_label} · {preview.board} Class {preview.class_level} · {preview.start_date_ist || preview.start_date} – {preview.end_date_ist || preview.end_date}
          </p>
          <div className="mt-2 space-y-0.5">
            {preview.subjects.map(s => (
              <p key={s.subject} className="text-[11px] font-body text-slate-600">
                <span className="font-heading text-slate-800">{s.subject}:</span> {s.chapters.length ? s.chapters.join(', ') : 'no chapters yet'}
              </p>
            ))}
          </div>
          {!preview.adoptable && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] font-body text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-2 py-1.5" data-testid="adopt-preview-blocked">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              {preview.archived
                ? 'This exam has been archived by whoever shared it, so it cannot be adopted.'
                : preview.already_yours
                  ? 'This exam is already on your list.'
                  : 'This blueprint is for a different board or class, so the chapters would not match your syllabus.'}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" className="h-7 text-[11px] bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
              disabled={!preview.adoptable || adopting} data-testid="adopt-preview-confirm" onClick={onAdopt}>
              {adopting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Adopt this exam
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid="adopt-preview-cancel" onClick={() => setPreview(null)}>Cancel</Button>
          </div>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2.5">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-slate-400 font-body" data-testid="exams-list-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading exams…</p>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="exams-error">
            <p className="text-sm text-rose-700 font-body">{error}</p>
            <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={load} data-testid="exams-retry-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Try again
            </Button>
          </div>
        ) : exams.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center" data-testid="exams-empty">
            <CalendarDays className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="font-heading text-sm font-semibold text-slate-800 mt-2">
              {status === 'archived' ? 'Nothing archived yet' : 'No exams yet'}
            </p>
            <p className="text-[11px] text-slate-500 font-body mt-1">
              {status === 'archived' ? 'Archived exams stay here for your records.' : 'Add the next unit test or term exam, then map its portion subject by subject.'}
            </p>
          </div>
        ) : (
          exams.map(e => <ExamCard key={e.exam_id} exam={e} mapperLive={mapperLive} importsLive={importsLive} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onMap={(x) => setMapping(x)} onPlan={(x) => setPlanning(x)} onResults={(x) => setScoring(x)} onOverrides={(x) => setOverriding(x)} onDuplicate={onDuplicate} onArchive={onArchive} onRestore={onRestore} />)
        )}
      </div>

      {/* Reviewable import from a pasted school notice (Phase 04) */}
      {importsLive && (
      <div className="mt-6 rounded-2xl bg-white ring-1 ring-amber-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="exam-import-panel">
        <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
        <div className="p-3 bg-gradient-to-br from-amber-50/60 to-transparent">
        <p className="text-[11px] font-heading uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <ClipboardPaste className="w-3 h-3 text-amber-500" /> <span className="text-amber-700">Import from a school notice</span>
          <InfoDot text={HELP.exams.import} title="How the import works" side="right" testId="hint-import" />
        </p>
        <p className="text-[11px] text-slate-500 font-body mt-1">
          No one to copy from? Paste the school's circular — or upload the PDF/photo the school sent — and
          we'll match every chapter against the approved syllabus. You review it all before an exam is created.
        </p>
        <input type="file" ref={noticeFileRef} accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; scanNotice(f); }} />
        <Button size="sm" variant="outline" className="h-7 text-[11px] mt-2" data-testid="exam-notice-scan-btn"
          disabled={scanning} onClick={() => noticeFileRef.current?.click()}>
          {scanning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ScanLine className="w-3 h-3 mr-1" />}
          {scanning ? 'Reading the notice…' : 'Scan a notice (PDF or photo)'}
        </Button>
        <textarea data-testid="exam-import-textarea" value={notice} onChange={e => setNotice(e.target.value)} rows={3}
          placeholder={'Paste the notice, one subject per line:\nBiology: Basic Biology, Cell: The Unit of Life'}
          className="w-full mt-2 p-2 rounded-xl border border-slate-200 text-[12px] font-body resize-y" />
        <div className="flex items-center gap-2 mt-2">
          <Hint text={HELP.exams.reviewProposals}>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid="exam-import-parse" onClick={parseNotice}>Review proposals</Button>
          </Hint>
          {draft && (
            <Hint text={HELP.exams.confirmImport}>
              <Button size="sm" className="h-7 text-[11px]" data-testid="exam-import-confirm" onClick={confirmDraft}>Confirm &amp; create</Button>
            </Hint>
          )}
          {draft && (
            <Hint text={HELP.exams.discardImport}>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid="exam-import-discard" onClick={() => setDraft(null)}>Discard</Button>
            </Hint>
          )}
        </div>
        {draft && (
          <div className="mt-2 space-y-1" data-testid="exam-import-review">
            {(draft.proposed_name || draft.proposed_start_date) && (
              <p className="text-[11px] font-body text-slate-600" data-testid="exam-import-proposed">
                Read from the notice: <span className="font-heading text-slate-800">{draft.proposed_name || 'unnamed exam'}</span>
                {draft.proposed_start_date && ` · ${istDate(draft.proposed_start_date)}${draft.proposed_end_date ? ` – ${istDate(draft.proposed_end_date)}` : ''}`}
              </p>
            )}
            {draft.proposals.map(p => (
              <p key={p.subject} className="text-[11px] font-body text-emerald-700">
                ✓ {p.subject}: {p.chapters.join(', ')}{p.marks ? ` · ${p.marks} marks` : ''}
              </p>
            ))}
            {draft.exceptions.map((x, i) => (
              <p key={`${x.line}-${i}`} className="text-[11px] font-body text-amber-700">! {x.reason}</p>
            ))}
            {(draft.notes || []).map((n, i) => (
              <p key={`note-${i}`} className="text-[11px] font-body text-slate-500">· {n}</p>
            ))}
            {!draft.proposals.length && <p className="text-[11px] text-rose-600 font-body">Nothing matched the approved syllabus — fix the notice and review again.</p>}
            {draft.status === 'confirmed' && (
              <Button size="sm" variant="outline" className="h-7 text-[11px] mt-1" data-testid="exam-import-rollback" onClick={rollbackImport}>
                <Undo2 className="w-3 h-3 mr-1" /> Roll back this import
              </Button>
            )}
          </div>
        )}
        </div>
      </div>
      )}

      <PortionMapperDialog open={!!mapping} onOpenChange={(o) => { if (!o) setMapping(null); }}
        exam={mapping} onSaved={(saved) => { if (saved?.exam_id) setMapping(saved); load(); }} />

      {planning && (
        <section className="mt-6 rounded-2xl bg-white ring-1 ring-indigo-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden"
          data-testid="exam-planner-inline" ref={plannerRef}>
          <div className="h-1 bg-gradient-to-r from-indigo-400 via-violet-400 to-fuchsia-400" />
          <div className="p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-heading uppercase tracking-widest text-slate-400">
                Study / Exam Planner
              </p>
              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-400"
                data-testid="exam-planner-close" onClick={() => setPlanning(null)}>Close</Button>
            </div>
            <StudyPlanner canManage examId={planning.exam_id} childId={studentId} />
          </div>
        </section>
      )}

      {scoring && (
        <ExamResultDialog open={!!scoring} onOpenChange={(o) => { if (!o) setScoring(null); }} exam={scoring} />
      )}

      <HolidayCalendarDialog open={holidaysOpen} onOpenChange={setHolidaysOpen} childId={studentId} />

      {overriding && importsLive && (
        <ExamOverridesDialog open={!!overriding} onOpenChange={(o) => { if (!o) setOverriding(null); }}
          exam={overriding} onSaved={() => load()} />
      )}

      <ExamFormDialog open={formOpen} onOpenChange={setFormOpen} exam={editing} student={student} onSaved={load} />
    </div>
  );
}
